import { CncReport, CncReportLocal } from '@gameonly/core';
import { Types } from 'mongoose';
import { useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { authenticatedFetch } from '../../authentication/authenticatedFetch';
import { downloadFile } from '../../project/ProjectsUtils';

const getReport = async (reportId?: string): Promise<CncReport | undefined> => {
  if (!reportId) return undefined;
  const response = await authenticatedFetch(
    `${process.env.REACT_APP_API_URL}/cnc-reports/${reportId}`,
  );
  if (!response.ok) return undefined;
  return await response.json();
};

const patchReport = async (
  reportId: string,
  body: BodyInit | null | undefined,
) => {
  const response = await authenticatedFetch(
    `${process.env.REACT_APP_API_URL}/cnc-reports/${reportId}`,
    {
      method: 'PATCH',
      body,
    },
  );
  return await response.json();
};

const updateLocals = async ({
  reportId,
  locals,
}: {
  reportId: string;
  locals: CncReportLocal[];
}) => {
  const response = await authenticatedFetch(
    `${process.env.REACT_APP_API_URL}/cnc-reports/${reportId}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ locals }),
    },
  );
  if (response.status < 200 || response.status >= 300) {
    throw await response.json();
  }
  return await response.json();
};

const addProrataLocal = async ({
  reportId,
  local,
}: {
  reportId: string;
  local: CncReportLocal;
}) => {
  const response = await authenticatedFetch(
    `${process.env.REACT_APP_API_URL}/cnc-reports/add-prorata/${reportId}`,
    {
      method: 'POST',
      body: JSON.stringify({ local }),
    },
  );
  if (response.status < 200 || response.status >= 300) {
    throw await response.json();
  }
  return await response.json();
};

const useReport = (reportId: string | undefined) => {
  const queryClient = useQueryClient();
  const ongoingMutations = useRef(0);

  const reportQuery = useQuery(
    ['cnc-report', reportId],
    () => getReport(reportId),
    {
      useErrorBoundary: true,
    },
  );

  const currentReport = reportQuery.data;

  const editReportMutation = useMutation(
    (report: CncReport) => {
      if (!reportId) {
        throw new Error('No reportId provided');
      }
      return patchReport(reportId, JSON.stringify(report));
    },
    {
      onMutate: async (newName) => {
        await queryClient.cancelQueries(['cnc-report', reportId]);
        const previousReport = queryClient.getQueryData([
          'cnc-report',
          reportId,
        ]);
        queryClient.setQueryData(['report', reportId], (old: any) => ({
          ...old,
          name: newName,
        }));
        return { previousReport };
      },
      onSuccess: () => {
        queryClient.invalidateQueries(['cnc-report', reportId]);
      },
      onSettled: (updatedReport) => {
        queryClient.setQueryData(['cnc-report', reportId], updatedReport);
        queryClient.invalidateQueries('cnc-reports');
      },
    },
  );

  const createLocalMutation = useMutation(
    ({ local }: { local: CncReportLocal }) => {
      if (!reportId) {
        throw new Error('No reportId provided');
      }
      if (!currentReport) {
        throw new Error('No report');
      }
      if (!local.prorata) {
        const updatedLocals: CncReportLocal[] = [
          ...currentReport?.locals,
          local,
        ];
        return updateLocals({ reportId, locals: updatedLocals });
      } else {
        return addProrataLocal({ reportId, local });
      }
    },
    {
      onSettled: (updatedReport) => {
        queryClient.setQueryData(['cnc-report', reportId], updatedReport);
        queryClient.invalidateQueries('cnc-reports');
      },
    },
  );

  const deleteLocalMutation = useMutation(
    ({ localId }: { localId: Types.ObjectId }) => {
      if (!reportId) {
        throw new Error('No reportId provided');
      }
      if (!currentReport) {
        throw new Error('No report');
      }
      const updatedLocals: CncReportLocal[] = currentReport.locals.filter(
        (local) => local.id !== localId,
      );
      return updateLocals({ reportId, locals: updatedLocals });
    },
    {
      onSettled: (updatedReport) => {
        queryClient.setQueryData(['cnc-report', reportId], updatedReport);
        queryClient.invalidateQueries('cnc-reports');
      },
    },
  );

  const validateReportMutation = useMutation(
    () => {
      if (!reportId) {
        throw new Error('No reportId provided');
      }
      return patchReport(reportId, JSON.stringify({ validated: true }));
    },
    {
      onMutate: async () => {
        ongoingMutations.current += 1;
        await queryClient.cancelQueries({ queryKey: ['cnc-report', reportId] });

        const previousReport = queryClient.getQueryData<CncReport>([
          'cnc-report',
          reportId,
        ]);

        const newReport = {
          ...previousReport,
          validated: true,
        };
        queryClient.setQueryData(['cnc-report', reportId], newReport);
      },
      onSettled: (updatedReport) => {
        ongoingMutations.current -= 1;
        ongoingMutations.current === 0 &&
          queryClient.setQueryData(['cnc-report', reportId], updatedReport);

        queryClient.invalidateQueries('cnc-reports');
      },
    },
  );
  const downloadReport = async () => {
    const reportId = reportQuery.data?._id;
    const CNCid = reportQuery.data?.CNCid;
    try {
      const response = await authenticatedFetch(
        `${process.env.REACT_APP_API_URL}/cnc-reports/${reportId}`,
        {
          method: 'GET',
          headers: new Headers({
            Accept: 'application/vnd.ms-excel',
            'Content-Type': 'application/json',
          }),
        },
      );
      if (!response.ok) {
        const errorDetails = await response.json();
        console.error('Failed to download report:', {
          status: response.status,
          statusText: response.statusText,
          errorDetails,
        });
        throw new Error(
          `Error ${response.status}: ${
            errorDetails.message || 'Unknown error'
          }`,
        );
      }
      const statisticFileBlob = await response.blob();
      const filename = `CNC-${CNCid}.xlsx`;
      downloadFile(statisticFileBlob, filename);
    } catch (error) {
      console.error('An error occurred while downloading the report:', error);
    }
  };

  return {
    report: reportQuery.data,
    createLocal: createLocalMutation.mutateAsync,
    deleteLocal: deleteLocalMutation.mutateAsync,
    validateReport: validateReportMutation.mutateAsync,
    editReport: editReportMutation.mutateAsync,
    downloadReport,
    loadingCreateLocal: createLocalMutation.isLoading,
    loadingValidateReport: validateReportMutation.isLoading,
    loading: reportQuery.isLoading,
    error: reportQuery.error,
  };
};

export default useReport;
